import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTenant } from '../contexts/TenantContext';
import { DashboardLayout } from '../layouts/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { 
    Select, 
    SelectContent, 
    SelectItem, 
    SelectTrigger, 
    SelectValue 
} from '../components/ui/select';
import { 
    BarChart3, 
    TrendingUp, 
    Users, 
    Award,
    Loader2
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Simple bar chart component
const SimpleBarChart = ({ data, color, maxValue }) => {
    if (!data || data.length === 0) return null;
    
    const max = maxValue || Math.max(...data.map(d => d.value), 1);
    
    return (
        <div className="space-y-2">
            {data.map((item, index) => (
                <div key={index} className="flex items-center gap-3">
                    <div className="w-20 text-xs text-slate-500 truncate">{item.label}</div>
                    <div className="flex-1 h-6 bg-slate-100 rounded-full overflow-hidden">
                        <div 
                            className="h-full rounded-full transition-all duration-500"
                            style={{ 
                                width: `${(item.value / max) * 100}%`,
                                backgroundColor: color
                            }}
                        />
                    </div>
                    <div className="w-12 text-sm font-medium text-right">{item.value}</div>
                </div>
            ))}
        </div>
    );
};

// Status distribution pie chart (simplified as bars)
const StatusChart = ({ data, colors }) => {
    if (!data || Object.keys(data).length === 0) return null;
    
    const total = Object.values(data).reduce((a, b) => a + b, 0);
    const statusLabels = {
        new: 'Neu',
        contacted: 'Kontaktiert',
        qualified: 'Qualifiziert',
        converted: 'Konvertiert',
        rejected: 'Abgelehnt'
    };
    
    return (
        <div className="space-y-3">
            {Object.entries(data).map(([status, count]) => {
                const percentage = total > 0 ? ((count / total) * 100).toFixed(1) : 0;
                return (
                    <div key={status} className="flex items-center gap-3">
                        <div className="w-24 text-sm text-slate-600">
                            {statusLabels[status] || status}
                        </div>
                        <div className="flex-1 h-4 bg-slate-100 rounded-full overflow-hidden">
                            <div 
                                className="h-full rounded-full transition-all duration-500"
                                style={{ 
                                    width: `${percentage}%`,
                                    backgroundColor: colors[status] || '#64748b'
                                }}
                            />
                        </div>
                        <div className="w-16 text-sm text-right">
                            <span className="font-medium">{count}</span>
                            <span className="text-slate-400 ml-1">({percentage}%)</span>
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

// Line chart simplified
const SimpleTrendChart = ({ data, color }) => {
    if (!data || data.length === 0) return null;
    
    const maxTotal = Math.max(...data.map(d => d.total), 1);
    
    return (
        <div className="space-y-1">
            <div className="flex items-end gap-1 h-32">
                {data.slice(-14).map((day, index) => {
                    const height = (day.total / maxTotal) * 100;
                    const convertedHeight = day.total > 0 ? (day.converted / day.total) * height : 0;
                    return (
                        <div 
                            key={index} 
                            className="flex-1 flex flex-col justify-end gap-0.5"
                            title={`${day.date}: ${day.total} Empfehlungen, ${day.converted} konvertiert`}
                        >
                            <div 
                                className="w-full rounded-t transition-all duration-300"
                                style={{ 
                                    height: `${height - convertedHeight}%`,
                                    backgroundColor: `${color}40`,
                                    minHeight: day.total > 0 ? '4px' : '0'
                                }}
                            />
                            <div 
                                className="w-full rounded-b transition-all duration-300"
                                style={{ 
                                    height: `${convertedHeight}%`,
                                    backgroundColor: color,
                                    minHeight: day.converted > 0 ? '4px' : '0'
                                }}
                            />
                        </div>
                    );
                })}
            </div>
            <div className="flex justify-between text-xs text-slate-400 mt-2">
                <span>{data.length > 0 ? data[Math.max(0, data.length - 14)]?.date : ''}</span>
                <span>{data.length > 0 ? data[data.length - 1]?.date : ''}</span>
            </div>
            <div className="flex items-center gap-4 mt-2 text-xs">
                <div className="flex items-center gap-1">
                    <div className="w-3 h-3 rounded" style={{ backgroundColor: `${color}40` }} />
                    <span className="text-slate-500">Gesamt</span>
                </div>
                <div className="flex items-center gap-1">
                    <div className="w-3 h-3 rounded" style={{ backgroundColor: color }} />
                    <span className="text-slate-500">Konvertiert</span>
                </div>
            </div>
        </div>
    );
};

export const AdminAnalyticsPage = () => {
    const { token } = useAuth();
    const { tenant } = useTenant();
    
    const [referralAnalytics, setReferralAnalytics] = useState(null);
    const [pointsAnalytics, setPointsAnalytics] = useState(null);
    const [loading, setLoading] = useState(true);
    const [days, setDays] = useState('30');

    useEffect(() => {
        fetchAnalytics();
    }, [days]);

    const fetchAnalytics = async () => {
        setLoading(true);
        try {
            const [referralsRes, pointsRes] = await Promise.all([
                axios.get(`${API}/admin/analytics/referrals?days=${days}`, {
                    headers: { Authorization: `Bearer ${token}` }
                }),
                axios.get(`${API}/admin/analytics/points?days=${days}`, {
                    headers: { Authorization: `Bearer ${token}` }
                })
            ]);
            setReferralAnalytics(referralsRes.data);
            setPointsAnalytics(pointsRes.data);
        } catch (error) {
            console.error('Failed to fetch analytics:', error);
            toast.error('Analytik konnte nicht geladen werden');
        } finally {
            setLoading(false);
        }
    };

    const statusColors = {
        new: '#3b82f6',
        contacted: '#f59e0b',
        qualified: '#8b5cf6',
        converted: '#22c55e',
        rejected: '#ef4444'
    };

    const totalLeads = referralAnalytics?.daily?.reduce((sum, d) => sum + d.total, 0) || 0;
    const totalConverted = referralAnalytics?.daily?.reduce((sum, d) => sum + d.converted, 0) || 0;
    const conversionRate = totalLeads > 0 ? ((totalConverted / totalLeads) * 100).toFixed(1) : 0;

    return (
        <DashboardLayout>
            <div className="space-y-8 animate-fade-in">
                {/* Header */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div>
                        <h1 className="text-3xl font-bold text-slate-900">Analytik</h1>
                        <p className="text-slate-600 mt-1">
                            Übersicht über Empfehlungen und Performance
                        </p>
                    </div>
                    <Select value={days} onValueChange={setDays}>
                        <SelectTrigger className="w-40">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="7">Letzte 7 Tage</SelectItem>
                            <SelectItem value="30">Letzte 30 Tage</SelectItem>
                            <SelectItem value="90">Letzte 90 Tage</SelectItem>
                            <SelectItem value="365">Letztes Jahr</SelectItem>
                        </SelectContent>
                    </Select>
                </div>

                {loading ? (
                    <div className="flex items-center justify-center py-20">
                        <Loader2 size={40} className="animate-spin text-slate-400" />
                    </div>
                ) : (
                    <>
                        {/* Summary Cards */}
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                            <Card>
                                <CardContent className="p-6">
                                    <div className="flex items-center gap-4">
                                        <div 
                                            className="w-12 h-12 rounded-xl flex items-center justify-center"
                                            style={{ backgroundColor: `${tenant?.primary_color}15` }}
                                        >
                                            <Users size={24} style={{ color: tenant?.primary_color }} />
                                        </div>
                                        <div>
                                            <p className="text-sm text-slate-500">Empfehlungen ({days} Tage)</p>
                                            <p className="text-2xl font-bold text-slate-900">
                                                {totalLeads.toLocaleString('de-DE')}
                                            </p>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>

                            <Card>
                                <CardContent className="p-6">
                                    <div className="flex items-center gap-4">
                                        <div className="w-12 h-12 rounded-xl bg-green-50 flex items-center justify-center">
                                            <TrendingUp size={24} className="text-green-600" />
                                        </div>
                                        <div>
                                            <p className="text-sm text-slate-500">Konvertiert</p>
                                            <p className="text-2xl font-bold text-slate-900">
                                                {totalConverted.toLocaleString('de-DE')}
                                            </p>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>

                            <Card>
                                <CardContent className="p-6">
                                    <div className="flex items-center gap-4">
                                        <div className="w-12 h-12 rounded-xl bg-purple-50 flex items-center justify-center">
                                            <BarChart3 size={24} className="text-purple-600" />
                                        </div>
                                        <div>
                                            <p className="text-sm text-slate-500">Konversionsrate</p>
                                            <p className="text-2xl font-bold text-slate-900">
                                                {conversionRate}%
                                            </p>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>
                        </div>

                        {/* Charts Row */}
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                            {/* Trend Chart */}
                            <Card>
                                <CardHeader>
                                    <CardTitle>Empfehlungs-Entwicklung</CardTitle>
                                    <CardDescription>Tägliche Empfehlungen und Konversionen</CardDescription>
                                </CardHeader>
                                <CardContent>
                                    {referralAnalytics?.daily?.length > 0 ? (
                                        <SimpleTrendChart 
                                            data={referralAnalytics.daily} 
                                            color={tenant?.primary_color || '#3b82f6'} 
                                        />
                                    ) : (
                                        <div className="text-center py-8 text-slate-400">
                                            Keine Daten für diesen Zeitraum
                                        </div>
                                    )}
                                </CardContent>
                            </Card>

                            {/* Status Distribution */}
                            <Card>
                                <CardHeader>
                                    <CardTitle>Status-Verteilung</CardTitle>
                                    <CardDescription>Alle Empfehlungen nach Status</CardDescription>
                                </CardHeader>
                                <CardContent>
                                    {referralAnalytics?.status_distribution && 
                                     Object.keys(referralAnalytics.status_distribution).length > 0 ? (
                                        <StatusChart 
                                            data={referralAnalytics.status_distribution} 
                                            colors={statusColors} 
                                        />
                                    ) : (
                                        <div className="text-center py-8 text-slate-400">
                                            Keine Daten verfügbar
                                        </div>
                                    )}
                                </CardContent>
                            </Card>
                        </div>

                        {/* Bottom Row */}
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                            {/* Top Referrers */}
                            <Card>
                                <CardHeader>
                                    <CardTitle>Top Empfehler</CardTitle>
                                    <CardDescription>Kunden mit den meisten Empfehlungen</CardDescription>
                                </CardHeader>
                                <CardContent>
                                    {referralAnalytics?.top_referrers?.length > 0 ? (
                                        <SimpleBarChart 
                                            data={referralAnalytics.top_referrers.map(r => ({
                                                label: r.name,
                                                value: r.count
                                            }))}
                                            color={tenant?.primary_color || '#3b82f6'}
                                        />
                                    ) : (
                                        <div className="text-center py-8 text-slate-400">
                                            Keine Daten verfügbar
                                        </div>
                                    )}
                                </CardContent>
                            </Card>

                            {/* Points Distribution */}
                            <Card>
                                <CardHeader>
                                    <CardTitle>Punkte-Verteilung</CardTitle>
                                    <CardDescription>
                                        Gesamt im Umlauf: {(pointsAnalytics?.total_points_in_circulation || 0).toLocaleString('de-DE')} Punkte
                                    </CardDescription>
                                </CardHeader>
                                <CardContent>
                                    {pointsAnalytics?.points_distribution?.length > 0 ? (
                                        <SimpleBarChart 
                                            data={pointsAnalytics.points_distribution.map(d => ({
                                                label: d.range + ' Pkt.',
                                                value: d.count
                                            }))}
                                            color="#22c55e"
                                        />
                                    ) : (
                                        <div className="text-center py-8 text-slate-400">
                                            Keine Daten verfügbar
                                        </div>
                                    )}
                                </CardContent>
                            </Card>
                        </div>
                    </>
                )}
            </div>
        </DashboardLayout>
    );
};
